import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import API_BASE from "../../config";
import styles from "./TimesheetPage.module.css";
import { useCompanyDefaults, resolveEmployeeWorkSettings } from "../../utils/companyDefaults";
import { isTimesheetDayOff } from "../../utils/timesheetDayOff";
import { getCurrentStoreName } from "../../utils/stores";
import { extractErrorMessage } from "../../utils/apiError";

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function getUser() {
  try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
  catch { return null; }
}

/** Identity headers for OT-approval endpoints. The backend reads
 *  X-User-Role for admin tier, X-Employee-Code for ownership / manager
 *  reachability, and X-Username for audit trail. */
function authHeaders(extra) {
  const u = getUser() || {};
  return {
    "Content-Type": "application/json",
    "X-User-Role": u.role || "",
    "X-Employee-Code": u.employeeCode || "",
    "X-Username": u.username || "system",
    ...(extra || {}),
  };
}

function fmt12(t) {
  if (!t) return "";
  const [h, m] = (typeof t === "string" ? t : "").split(":").map(Number);
  if (isNaN(h)) return "";
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function fmtShort(t) {
  if (!t) return "";
  const [h, m] = (typeof t === "string" ? t : "").split(":").map(Number);
  if (isNaN(h)) return "";
  const ampm = h >= 12 ? "PM" : "AM";
  const mm = m > 0 ? `:${String(m).padStart(2, "0")}` : "";
  return `${h % 12 || 12}${mm}${ampm}`;
}

function buildGrid(year, month) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDow = (first.getDay() + 6) % 7; // Mon=0

  const cells = [];
  const prevDays = new Date(year, month, 0).getDate();

  for (let i = startDow - 1; i >= 0; i--)
    cells.push({ day: prevDays - i, month: month === 0 ? 11 : month - 1, year: month === 0 ? year - 1 : year, muted: true });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ day: d, month, year, muted: false });
  const nm = month === 11 ? 0 : month + 1, ny = month === 11 ? year + 1 : year;
  let n = 1;
  while (cells.length < 42) cells.push({ day: n++, month: nm, year: ny, muted: true });

  return cells;
}

function dateKey(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

// ── Friendly time picker (Hour / Minute / AM-PM dropdowns) ──
function TimePicker({ value, onChange, label, readOnly = false }) {
  let hr12 = "", min = "", ampm = "AM";
  if (value) {
    const [h, m] = value.split(":").map(Number);
    ampm = h >= 12 ? "PM" : "AM";
    hr12 = String(h % 12 || 12);
    min = String(m).padStart(2, "0");
  }

  function update(newHr, newMin, newAmpm) {
    if (readOnly) return;
    const h = newHr === "" ? "" : newHr;
    const m = newMin === "" ? "00" : newMin;
    const ap = newAmpm || ampm;
    if (h === "") { onChange(""); return; }
    let h24 = parseInt(h, 10);
    if (ap === "PM" && h24 !== 12) h24 += 12;
    if (ap === "AM" && h24 === 12) h24 = 0;
    onChange(`${String(h24).padStart(2, "0")}:${m}`);
  }

  const hours = ["","1","2","3","4","5","6","7","8","9","10","11","12"];
  const minutes = ["00","05","10","15","20","25","30","35","40","45","50","55"];

  return (
    <div className={styles.field}>
      <label className={styles.label}>{label}</label>
      <div className={styles.timePickerRow}>
        <select className={styles.timeSelect} value={hr12} disabled={readOnly}
                onChange={e => update(e.target.value, min, ampm)}>
          <option value="">--</option>
          {hours.filter(Boolean).map(h => <option key={h} value={h}>{h}</option>)}
        </select>
        <span className={styles.timeColon}>:</span>
        <select className={styles.timeSelect} value={min} disabled={readOnly || !hr12}
                onChange={e => update(hr12, e.target.value, ampm)}>
          {minutes.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className={styles.timeAmpm} value={ampm} disabled={readOnly || !hr12}
                onChange={e => update(hr12, min, e.target.value)}>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}

function statusColor(s) {
  if (!s) return "rgba(0,0,0,.15)";
  const l = s.toLowerCase();
  if (l === "present") return "#27ae60";
  if (l === "absent") return "#7f8c8d";
  if (l === "leave") return "#c0392b";
  if (l === "holiday") return "#e74c3c";
  return "#95a5a6";
}

function statusBg(s) {
  if (!s) return "transparent";
  const l = s.toLowerCase();
  if (l === "present") return "rgba(39,174,96,.12)";
  if (l === "absent") return "rgba(127,140,141,.15)";
  if (l === "leave") return "rgba(192,57,43,.12)";
  if (l === "holiday") return "rgba(231,76,60,.12)";
  return "transparent";
}

// Derived background: for Present entries, late/undertime/overtime
// override the plain-green tint so the calendar actually reflects the
// edited metrics. Non-Present statuses keep their own color. When
// `suppressPenalties` is true the late/undertime tints are skipped
// so day-off entries (Sundays, Holiday status) on "don't deduct on
// day-off" employees don't flash orange/pink.
function cellBg(entry, suppressPenalties) {
  if (!entry) return "transparent";
  if (entry.status === "Present") {
    if (!suppressPenalties && (entry.undertimeMinutes || 0) > 0) return "rgba(231,76,60,.14)";   // pink/red — undertime
    if (!suppressPenalties && (entry.lateMinutes || 0) > 0)       return "rgba(230,126,34,.16)"; // orange — late
    if ((entry.overtimeHours || 0) > 0)                           return "rgba(41,128,185,.14)"; // blue — overtime
  }
  return statusBg(entry.status);
}

// ── Add/Edit Timesheet Modal ──
function TimesheetModal({ entry, employeeCode, onClose, onSaved }) {
  const isEdit = !!entry;
  // Per-employee effective work settings, resolved against the company
  // defaults (from /api/settings via useCompanyDefaults). Employees
  // with null override fields inherit whatever the admin configured on
  // the Settings → Timesheet tab.
  const companyDefaults = useCompanyDefaults();
  const [empSchedule, setEmpSchedule] = useState({ start: "08:00", end: "17:00" });
  // Grace period (mins past schedEnd) before OT starts accruing.
  // Lives in a ref so recompute() always sees the latest value
  // without being recreated on each render.
  const otGraceRef = useRef(30);
  // Effective work-time policy: "STRICT" or "FLEXIBLE". Kept in a ref
  // so recompute() always sees the latest resolved value.
  const policyRef = useRef("STRICT");
  // Whether to apply late / undertime deductions on day-off rows
  // (Sundays + Holiday status). Resolved against the company default
  // at modal open time. Kept in a ref for the same reason as above.
  const applyDayOffRef = useRef(false);
  const [form, setForm] = useState({
    date: "", endDate: "", schedStart: "08:00", schedEnd: "17:00",
    timeIn: "", timeOut: "", breakMinutes: 60,
    workedHours: 0, lateMinutes: 0, undertimeMinutes: 0,
    overtimeHours: 0, status: "Present", remarks: "",
  });
  const [useRange, setUseRange] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState("");
  const overlayRef = useRef(null);

  // Fetch the employee's expected schedule once per open, then resolve
  // any null override fields against the company defaults so the modal
  // always has a complete, consistent view of the employee's schedule.
  useEffect(() => {
    if (!employeeCode) return;
    fetch(`${API_BASE}/api/employees/${employeeCode}`)
      .then(r => r.ok ? r.json() : null)
      .then(emp => {
        if (!emp) return;
        const resolved = resolveEmployeeWorkSettings(emp, companyDefaults);
        setEmpSchedule({ start: resolved.schedStart, end: resolved.schedEnd });
        otGraceRef.current    = Number(resolved.overtimeStartMinutes) || 0;
        policyRef.current     = resolved.workTimePolicy || "STRICT";
        applyDayOffRef.current = !!resolved.applyDeductionsOnDayOff;
        // Seed the form's schedule fields only if we're adding a new
        // entry. For edits we keep the historical schedule recorded
        // on the entry itself (set by the `entry` useEffect below).
        if (!entry) {
          setForm(f => recompute({ ...f, schedStart: resolved.schedStart, schedEnd: resolved.schedEnd }));
        } else {
          // For an existing entry, re-run recompute so the fetched OT
          // grace + policy are applied to the already-loaded time values.
          setForm(f => recompute({ ...f }));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeCode, entry, companyDefaults]);

  useEffect(() => {
    if (entry) {
      // Seed from entry, then immediately recompute so late/undertime/OT
      // reflect the current times + schedule even if the stored values
      // are stale (e.g. entries created before these metrics existed).
      setForm(recompute({
        date: entry.date || "",
        endDate: "",
        schedStart: entry.schedStart || empSchedule.start,
        schedEnd:   entry.schedEnd   || empSchedule.end,
        timeIn: entry.timeIn || "",
        timeOut: entry.timeOut || "",
        breakMinutes: entry.breakMinutes ?? 60,
        workedHours: entry.workedHours ?? 0,
        lateMinutes: entry.lateMinutes ?? 0,
        undertimeMinutes: entry.undertimeMinutes ?? 0,
        overtimeHours: entry.overtimeHours ?? 0,
        status: entry.status || "Present",
        remarks: entry.remarks || "",
      }));
      setUseRange(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  // Escape closes the modal (outside-click no longer does, to avoid
  // losing in-progress form data).
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function set(k, v) {
    setForm(f => {
      const next = { ...f, [k]: v };
      // Auto-compute whenever time fields change
      return recompute(next);
    });
  }

  function toMin(t) {
    if (!t) return null;
    const [h, m] = t.split(":").map(Number);
    return isNaN(h) ? null : h * 60 + (m || 0);
  }

  function recompute(f) {
    const schedIn  = toMin(f.schedStart);
    const schedOut = toMin(f.schedEnd);
    const tin      = toMin(f.timeIn);
    const tout     = toMin(f.timeOut);
    const brk      = Number(f.breakMinutes) || 0;
    const policy   = policyRef.current || "STRICT";
    const isFlex   = policy === "FLEXIBLE";

    // Weekend / holiday override — on Sundays and Holiday-status
    // entries, late/undertime penalties are skipped UNLESS the
    // employee's effective settings say deductions apply on day-off
    // rows. Delegates to the shared helper so the check handles both
    // string and array date shapes returned by the backend.
    const isDayOff = !applyDayOffRef.current &&
      isTimesheetDayOff({ date: f.date, status: f.status });

    // Late
    //   Day off  — 0 (no penalty on weekends/holidays).
    //   STRICT   — minutes past scheduled start (≥ 0).
    //   FLEXIBLE — always 0 (come early, go home early; no late penalty).
    if (isDayOff || isFlex) {
      f.lateMinutes = 0;
    } else {
      let late = 0;
      if (schedIn !== null && tin !== null && tin > schedIn) late = tin - schedIn;
      f.lateMinutes = late;
    }

    // Worked hours / overtime / undertime.
    if (tin !== null && tout !== null && tout > tin) {
      const totalMin     = tout - tin - brk;
      const worked       = Math.max(0, totalMin / 60);
      const schedHours   = (schedIn !== null && schedOut !== null) ? (schedOut - schedIn - brk) / 60 : 8;
      const otGraceMin   = Number(otGraceRef.current) || 0;

      f.workedHours = Math.round(worked * 10) / 10;

      // Overtime:
      //   STRICT   — minutes past (schedEnd + grace).
      //   FLEXIBLE — minutes past (tin + scheduled gross duration + grace).
      //              e.g. shift is 9-5, you clock in at 7 → any work
      //              past 3 PM + grace counts as OT.
      if (schedIn !== null && schedOut !== null) {
        const otThreshold = isFlex
          ? tin + (schedOut - schedIn) + otGraceMin
          : schedOut + otGraceMin;
        const otMin = Math.max(0, tout - otThreshold);
        f.overtimeHours = Math.round((otMin / 60) * 10) / 10;
      } else {
        f.overtimeHours = 0;
      }

      f.undertimeMinutes = (!isDayOff && worked < schedHours) ? Math.round((schedHours - worked) * 60) : 0;
    } else {
      f.workedHours = 0;
      f.overtimeHours = 0;
      f.undertimeMinutes = 0;
    }
    return f;
  }

  async function save() {
    if (!form.date) { setError("Start date is required."); return; }
    if (useRange && !isEdit && !form.endDate) { setError("End date is required for range."); return; }

    setSaving(true); setError(""); setProgress("");
    try {
      if (useRange && !isEdit) {
        // Bulk create: one entry per weekday in range
        const start = new Date(form.date + "T00:00:00");
        const end = new Date(form.endDate + "T00:00:00");
        if (end < start) { setError("End date must be after start date."); setSaving(false); return; }

        const dates = [];
        const cur = new Date(start);
        while (cur <= end) {
          const dow = cur.getDay();
          if (dow !== 0) { // skip Sundays
            dates.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`);
          }
          cur.setDate(cur.getDate() + 1);
        }

        for (let i = 0; i < dates.length; i++) {
          setProgress(`Creating ${i + 1} of ${dates.length}...`);
          const body = {
            employeeCode,
            date: dates[i],
            schedStart: form.schedStart || null,
            schedEnd: form.schedEnd || null,
            timeIn: form.timeIn || null,
            timeOut: form.timeOut || null,
            breakMinutes: Number(form.breakMinutes) || 0,
            workedHours: Number(form.workedHours) || 0,
            lateMinutes: Number(form.lateMinutes) || 0,
            undertimeMinutes: Number(form.undertimeMinutes) || 0,
            overtimeHours: Number(form.overtimeHours) || 0,
            status: form.status,
            remarks: form.remarks.trim() || null,
          };
          await fetch(`${API_BASE}/api/timesheets`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify(body),
          });
        }
      } else {
        // Single entry
        const body = {
          employeeCode,
          date: form.date,
          schedStart: form.schedStart || null,
          schedEnd: form.schedEnd || null,
          timeIn: form.timeIn || null,
          timeOut: form.timeOut || null,
          breakMinutes: Number(form.breakMinutes) || 0,
          workedHours: Number(form.workedHours) || 0,
          lateMinutes: Number(form.lateMinutes) || 0,
          undertimeMinutes: Number(form.undertimeMinutes) || 0,
          overtimeHours: Number(form.overtimeHours) || 0,
          status: form.status,
          remarks: form.remarks.trim() || null,
        };
        const url = isEdit ? `${API_BASE}/api/timesheets/${entry.id}` : `${API_BASE}/api/timesheets`;
        await fetch(url, {
          method: isEdit ? "PUT" : "POST",
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
      }
      onSaved();
    } catch {
      setError("Failed to save.");
    } finally {
      setSaving(false);
      setProgress("");
    }
  }

  return createPortal(
    <div className={styles.overlay} ref={overlayRef}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>{isEdit ? "Edit Timesheet" : "+ Add Timesheet"}</div>
          <button className={styles.modalClose} onClick={onClose}>✕</button>
        </div>
        <div className={styles.modalBody}>
          {/* Range toggle — only for new entries */}
          {!isEdit && (
            <label className={styles.rangeToggle} onClick={() => setUseRange(r => !r)}>
              <div className={`${styles.toggleTrack} ${useRange ? styles.toggleOn : ""}`}>
                <div className={styles.toggleThumb} />
              </div>
              <span className={styles.toggleText}>Date Range {useRange ? "(bulk create)" : ""}</span>
            </label>
          )}

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label}>{useRange && !isEdit ? "Start Date *" : "Date *"}</label>
              <input className={styles.input} type="date" value={form.date} onChange={e => set("date", e.target.value)} />
            </div>
            {useRange && !isEdit ? (
              <div className={styles.field}>
                <label className={styles.label}>End Date *</label>
                <input className={styles.input} type="date" value={form.endDate} onChange={e => set("endDate", e.target.value)} />
              </div>
            ) : (
              <div className={styles.field}>
                <label className={styles.label}>Status</label>
                <select className={styles.input} value={form.status} onChange={e => set("status", e.target.value)}>
                  <option value="Present">Present</option>
                  <option value="Absent">Absent</option>
                  <option value="Leave">Leave</option>
                  <option value="Holiday">Holiday</option>
                </select>
              </div>
            )}
          </div>

          {useRange && !isEdit && (
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.label}>Status</label>
                <select className={styles.input} value={form.status} onChange={e => set("status", e.target.value)}>
                  <option value="Present">Present</option>
                  <option value="Absent">Absent</option>
                  <option value="Leave">Leave</option>
                  <option value="Holiday">Holiday</option>
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>&nbsp;</label>
                <div className={styles.rangeHint}>Sundays are automatically skipped</div>
              </div>
            </div>
          )}

          <div className={styles.fieldRow}>
            {/* Scheduled hours are driven by the employee's expectedStart /
                expectedEnd fields (set from Add/Edit Employee). Read-only
                here so every timesheet entry uses the same shift. */}
            <TimePicker label="Scheduled Start" value={form.schedStart} onChange={() => {}} readOnly />
            <TimePicker label="Scheduled End"   value={form.schedEnd}   onChange={() => {}} readOnly />
          </div>

          <div className={styles.fieldRow}>
            <TimePicker label="Time In" value={form.timeIn} onChange={v => set("timeIn", v)} />
            <TimePicker label="Time Out" value={form.timeOut} onChange={v => set("timeOut", v)} />
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label}>Break (mins)</label>
              <input className={styles.input} type="number" value={form.breakMinutes} onChange={e => set("breakMinutes", e.target.value)} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Worked Hours</label>
              <input className={`${styles.input} ${styles.inputReadonly}`} type="text" value={form.workedHours} readOnly />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label}>Late (mins)</label>
              <input className={`${styles.input} ${styles.inputReadonly}`} type="text" value={form.lateMinutes} readOnly />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Undertime (mins)</label>
              <input className={`${styles.input} ${styles.inputReadonly}`} type="text" value={form.undertimeMinutes} readOnly />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.label}>Overtime (hrs)</label>
              <input className={`${styles.input} ${styles.inputReadonly}`} type="text" value={form.overtimeHours} readOnly />
            </div>
            <div className={styles.field} />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Remarks</label>
            <textarea className={styles.textarea} value={form.remarks} onChange={e => set("remarks", e.target.value)} placeholder="Traffic delay, etc." />
          </div>
        </div>

        {error && <div className={styles.modalError}>{error}</div>}
        {progress && <div className={styles.modalProgress}>{progress}</div>}
        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.saveBtn} onClick={save} disabled={saving}>
            {saving ? (progress || "Saving...") : isEdit ? "Save Changes" : useRange ? "Create All" : "+ Add Timesheet"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Main Page ──
export default function TimesheetPage() {
  const user = getUser();
  const isAdmin = user?.role === "Admin"
               || user?.role === "SuperAdmin"
               || user?.role === "Owner";
  const today = new Date();

  const [employees, setEmployees] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [modalEntry, setModalEntry] = useState(undefined); // undefined=closed, null=add, obj=edit
  const [monthDropOpen, setMonthDropOpen] = useState(false);
  const monthDropRef = useRef(null);
  const [empSearch, setEmpSearch] = useState("");

  // Effective work settings for the selected employee, resolved
  // against the current company defaults. Used by the calendar cell
  // renderer and the right-panel detail display so late/undertime
  // suppression on day-off entries matches the employee's policy.
  const companyDefaults = useCompanyDefaults();
  const [effectiveSettings, setEffectiveSettings] = useState(() =>
    resolveEmployeeWorkSettings(null, companyDefaults)
  );
  useEffect(() => {
    if (!selectedCode) {
      setEffectiveSettings(resolveEmployeeWorkSettings(null, companyDefaults));
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/employees/${selectedCode}`)
      .then(r => r.ok ? r.json() : null)
      .then(emp => {
        if (cancelled) return;
        setEffectiveSettings(resolveEmployeeWorkSettings(emp, companyDefaults));
      })
      .catch(() => {
        if (!cancelled) setEffectiveSettings(resolveEmployeeWorkSettings(null, companyDefaults));
      });
    return () => { cancelled = true; };
  }, [selectedCode, companyDefaults]);

  // Per-entry helper: should we hide late/undertime for this row?
  // True when the entry is a day-off (Sunday / Holiday) AND the
  // employee's effective settings say deductions do NOT apply on
  // day-off entries. When applyDeductionsOnDayOff is true the stored
  // values are shown like any other day.
  const suppressDayOffPenalties = (entry) =>
    isTimesheetDayOff(entry) && !effectiveSettings.applyDeductionsOnDayOff;

  // Load employees. Admin-tier sees the store-scoped list; everyone
  // else pulls the unfiltered (?all=true) list so a manager can see
  // their reports even if they're in another store.
  useEffect(() => {
    const url = isAdmin
      ? `${API_BASE}/api/employees${(() => {
          const store = getCurrentStoreName();
          return store ? `?storeName=${encodeURIComponent(store)}` : "";
        })()}`
      : `${API_BASE}/api/employees?all=true`;
    fetch(url)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setEmployees(list);
        // Default selection: admin → first in list; non-admin → self
        // (so their own timesheet shows immediately).
        if (!selectedCode) {
          if (!isAdmin && user?.employeeCode) setSelectedCode(user.employeeCode);
          else if (list.length > 0)           setSelectedCode(list[0].code);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Non-admin users with at least one direct report are treated as
  // managers — they get a scoped sidebar showing their team.
  const myReports = useMemo(
    () => (user?.employeeCode
      ? (employees || []).filter(e => e.reportsTo === user.employeeCode)
      : []),
    [employees, user?.employeeCode]
  );
  const isManager  = !isAdmin && myReports.length > 0;
  const showSidebar = isAdmin || isManager;

  // Load timesheet entries
  useEffect(() => {
    if (!selectedCode) return;
    fetch(`${API_BASE}/api/timesheets?employeeCode=${selectedCode}&year=${year}&month=${month + 1}`, {
      headers: authHeaders(),
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setEntries(Array.isArray(data) ? data : []))
      .catch(() => setEntries([]));
  }, [selectedCode, year, month]);

  // Close month dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (monthDropRef.current && !monthDropRef.current.contains(e.target)) setMonthDropOpen(false);
    }
    if (monthDropOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [monthDropOpen]);

  const cells = useMemo(() => buildGrid(year, month), [year, month]);

  // Map entries by date
  const entryMap = useMemo(() => {
    const m = {};
    for (const e of entries) {
      const d = Array.isArray(e.date) ? `${e.date[0]}-${String(e.date[1]).padStart(2,"0")}-${String(e.date[2]).padStart(2,"0")}` : e.date;
      m[d] = e;
    }
    return m;
  }, [entries]);

  const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());
  const selectedEmployee = employees.find(e => e.code === selectedCode);

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1);
  }
  function goToday() { setYear(today.getFullYear()); setMonth(today.getMonth()); }

  function reload() {
    setModalEntry(undefined);
    setSelectedEntry(null);
    // Notify other pages (e.g. Salary) that timesheet data changed so
    // they can refetch their own caches.
    window.dispatchEvent(new Event("timesheet:changed"));
    // re-fetch
    if (!selectedCode) return;
    fetch(`${API_BASE}/api/timesheets?employeeCode=${selectedCode}&year=${year}&month=${month + 1}`, {
      headers: authHeaders(),
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setEntries(Array.isArray(data) ? data : []))
      .catch(() => setEntries([]));
    // Refresh the OT pending-count badges + records modal data, so an
    // approve / reject / cancel action lands immediately.
    if (showSidebar) loadOvertimeRecords();
  }

  async function handleDelete() {
    if (!selectedEntry?.id) return;
    await fetch(`${API_BASE}/api/timesheets/${selectedEntry.id}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    reload();
  }

  // ── Overtime approval workflow ─────────────────────────────────
  // Submit / cancel are owner-only. Decide is manager-or-admin. The
  // backend re-checks all three; the UI hides the buttons that wouldn't
  // apply so a curious user doesn't get a 403 popup.
  const [otRecordsOpen, setOtRecordsOpen] = useState(false);
  const [otRecords, setOtRecords] = useState([]);
  const [otRecordsLoading, setOtRecordsLoading] = useState(false);
  // Map of employeeCode → number of PENDING OT requests visible to the
  // current viewer. Drives the red badge on each sidebar row so a
  // manager / admin sees at a glance who is asking for approval.
  const [pendingByEmp, setPendingByEmp] = useState({});

  async function handleRequestOvertime() {
    if (!selectedEntry?.id) return;
    const r = await fetch(`${API_BASE}/api/timesheets/${selectedEntry.id}/request-overtime`, {
      method: "POST",
      headers: authHeaders(),
    });
    if (!r.ok) { alert(await extractErrorMessage(r, "Failed to submit overtime request.")); return; }
    const updated = await r.json();
    setSelectedEntry(updated);
    reload();
    window.dispatchEvent(new CustomEvent("approvals:changed"));
  }

  async function handleCancelOvertime() {
    if (!selectedEntry?.id) return;
    if (!window.confirm("Cancel this overtime request?")) return;
    const r = await fetch(`${API_BASE}/api/timesheets/${selectedEntry.id}/cancel-overtime`, {
      method: "POST",
      headers: authHeaders(),
    });
    if (!r.ok) { alert(await extractErrorMessage(r, "Failed to cancel.")); return; }
    const updated = await r.json();
    setSelectedEntry(updated);
    reload();
    window.dispatchEvent(new CustomEvent("approvals:changed"));
  }

  async function handleDecideOvertime(decision) {
    if (!selectedEntry?.id) return;
    let note = null;
    if (decision === "REJECTED") {
      note = window.prompt("Reason for rejecting (optional):") || null;
    }
    const r = await fetch(`${API_BASE}/api/timesheets/${selectedEntry.id}/decide-overtime`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ decision, note }),
    });
    if (!r.ok) { alert(await extractErrorMessage(r, "Failed to decide.")); return; }
    const updated = await r.json();
    setSelectedEntry(updated);
    reload();
    window.dispatchEvent(new CustomEvent("approvals:changed"));
  }

  async function loadOvertimeRecords() {
    setOtRecordsLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/timesheets/overtime-records`, {
        headers: authHeaders(),
      });
      if (!r.ok) { setOtRecords([]); setPendingByEmp({}); return; }
      const rows = await r.json();
      const list = Array.isArray(rows) ? rows : [];
      setOtRecords(list);
      const counts = {};
      for (const row of list) {
        if (row.overtimeStatus === "PENDING" && row.employeeCode) {
          counts[row.employeeCode] = (counts[row.employeeCode] || 0) + 1;
        }
      }
      setPendingByEmp(counts);
    } catch {
      setOtRecords([]);
      setPendingByEmp({});
    } finally {
      setOtRecordsLoading(false);
    }
  }

  // Refresh OT pending-count badges whenever the sidebar is visible
  // (admin / manager view) and on every reload triggered by save /
  // approve / reject / cancel.
  useEffect(() => {
    if (!showSidebar) return;
    loadOvertimeRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidebar, employees.length]);

  function openOvertimeRecords() {
    setOtRecordsOpen(true);
    loadOvertimeRecords();
  }

  // Admin sees everyone; manager sees self + direct reports.
  const sidebarPool = isAdmin
    ? employees
    : (() => {
        const self = user?.employeeCode
          ? employees.find(e => e.code === user.employeeCode)
          : null;
        return self ? [self, ...myReports] : myReports;
      })();
  const filteredEmps = empSearch
    ? sidebarPool.filter(e => (e.name || "").toLowerCase().includes(empSearch.toLowerCase()))
    : sidebarPool;

  const todayEntry = entryMap[todayKey];
  const canTimeIn = !todayEntry || !todayEntry.timeIn;
  const canTimeOut = todayEntry && todayEntry.timeIn && !todayEntry.timeOut;

  function nowTime() {
    const n = new Date();
    return `${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`;
  }

  async function handleTimeIn() {
    if (!selectedCode) return;
    const now = nowTime();
    const body = {
      employeeCode: selectedCode,
      date: todayKey,
      schedStart: "08:00", schedEnd: "17:00",
      timeIn: now, timeOut: null,
      breakMinutes: 60, workedHours: 0,
      lateMinutes: 0, undertimeMinutes: 0, overtimeHours: 0,
      status: "Present", remarks: null,
    };
    // Compute late
    const [sh] = "08:00".split(":").map(Number);
    const [nh, nm] = now.split(":").map(Number);
    const diffMin = (nh * 60 + nm) - (sh * 60);
    if (diffMin > 0) body.lateMinutes = diffMin;

    await fetch(`${API_BASE}/api/timesheets`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    reload();
  }

  async function handleTimeOut() {
    if (!todayEntry?.id) return;
    const now = nowTime();
    const [ih, im] = (todayEntry.timeIn || "08:00").split(":").map(Number);
    const [oh, om] = now.split(":").map(Number);
    const totalMin = (oh * 60 + om) - (ih * 60 + im);
    const breakMin = todayEntry.breakMinutes || 60;
    const worked = Math.max(0, (totalMin - breakMin) / 60);
    const schedHours = 8; // standard
    const ot = Math.max(0, worked - schedHours);

    await fetch(`${API_BASE}/api/timesheets/${todayEntry.id}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({
        timeOut: now,
        workedHours: Math.round(worked * 10) / 10,
        overtimeHours: Math.round(ot * 10) / 10,
        undertimeMinutes: worked < schedHours ? Math.round((schedHours - worked) * 60) : 0,
      }),
    });
    reload();
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>Timesheet</h1>
          <div className={styles.clockBtns}>
            <button
              className={`${styles.clockBtn} ${styles.clockIn}`}
              disabled={!canTimeIn}
              onClick={handleTimeIn}
            >
              Time In
            </button>
            <button
              className={`${styles.clockBtn} ${styles.clockOut}`}
              disabled={!canTimeOut}
              onClick={handleTimeOut}
            >
              Time Out
            </button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {showSidebar && (
            <button
              onClick={openOvertimeRecords}
              title="Overtime Approval Records"
              style={{
                padding: "8px 14px", border: "1px solid #d1d5db",
                borderRadius: 999, background: "#fff", color: "#111827",
                fontWeight: 600, fontSize: 13, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}>
              📋 Records
            </button>
          )}
          <button className={styles.addBtn} onClick={() => setModalEntry(null)}>+ Add Timesheet</button>
        </div>
      </div>

      <div className={`${styles.content} ${showSidebar ? styles.contentWithSidebar : ""}`}>
        {/* ── Sidebar: Employee list ── */}
        {showSidebar && (
          <div className={styles.sidebar}>
            <div className={styles.sideSearch}>
              <input
                className={styles.sideSearchInput}
                placeholder="Search employee..."
                value={empSearch}
                onChange={e => setEmpSearch(e.target.value)}
              />
            </div>
            <div className={styles.sideList}>
              {filteredEmps.map(emp => {
                const pending = pendingByEmp[emp.code] || 0;
                return (
                  <div
                    key={emp.code}
                    className={`${styles.sideItem} ${emp.code === selectedCode ? styles.sideItemActive : ""}`}
                    onClick={() => { setSelectedCode(emp.code); setSelectedEntry(null); }}
                  >
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      {emp.photoUrl
                        ? <img src={emp.photoUrl} alt={emp.name} className={styles.sideAvatar} />
                        : <div className={styles.sideAvatarFallback}>{(emp.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}</div>
                      }
                      {pending > 0 && (
                        <span
                          title={`${pending} pending overtime request${pending === 1 ? "" : "s"}`}
                          style={{
                            position: "absolute", top: -4, right: -4,
                            minWidth: 18, height: 18, padding: "0 5px",
                            borderRadius: 999, background: "#dc2626", color: "#fff",
                            fontSize: 10, fontWeight: 700, lineHeight: "18px",
                            textAlign: "center", boxShadow: "0 0 0 2px #fff",
                          }}>
                          {pending}
                        </span>
                      )}
                    </div>
                    <div className={styles.sideInfo}>
                      <div className={styles.sideName}>{emp.name}</div>
                      <div className={styles.sideRole}>
                        {emp.role}
                        {pending > 0 && (
                          <span style={{ marginLeft: 6, color: "#dc2626", fontWeight: 600 }}>
                            · {pending} pending
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Calendar ── */}
        <div className={styles.calendarCard}>
          <div className={styles.calToolbar}>
            <button className={styles.navArrow} onClick={prevMonth}>&#8249;</button>
            <div className={styles.monthPickerWrap} ref={monthDropRef}>
              <div className={styles.monthLabel} onClick={() => setMonthDropOpen(o => !o)}>
                {MONTH_NAMES[month]} {year} <span style={{ fontSize: 10, marginLeft: 2 }}>&#9662;</span>
              </div>
              {monthDropOpen && (
                <div className={styles.monthDropdown}>
                  <div className={styles.monthDropYear}>
                    <button className={styles.monthDropArrow} onClick={() => setYear(y => y - 1)}>&#8249;</button>
                    <span className={styles.monthDropYearLabel}>{year}</span>
                    <button className={styles.monthDropArrow} onClick={() => setYear(y => y + 1)}>&#8250;</button>
                  </div>
                  <div className={styles.monthDropGrid}>
                    {MONTH_NAMES.map((m, i) => (
                      <button key={m}
                        className={`${styles.monthDropItem} ${i === month ? styles.monthDropActive : ""}`}
                        onClick={() => { setMonth(i); setMonthDropOpen(false); }}>
                        {m.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button className={styles.navArrow} onClick={nextMonth}>&#8250;</button>
            <button className={styles.todayBtn} onClick={goToday} title="Today">&#9201;</button>
          </div>

          {/* Day-of-week header */}
          <div className={styles.dow}>
            {DOW.map(d => (
              <div key={d} className={`${styles.dowCell} ${d === "Sat" || d === "Sun" ? styles.dowWknd : ""}`}>{d}</div>
            ))}
          </div>

          {/* Grid */}
          <div className={styles.grid}>
            {cells.map((cell, idx) => {
              const key = dateKey(cell.year, cell.month, cell.day);
              const isToday = key === todayKey;
              const entry = entryMap[key];
              const hasEntry = !!entry;

              return (
                <div
                  key={idx}
                  className={`${styles.dayCell} ${cell.muted ? styles.dayMuted : ""} ${isToday ? styles.dayToday : ""} ${hasEntry ? styles.dayHasEntry : ""}`}
                  style={hasEntry && !cell.muted ? { background: cellBg(entry, suppressDayOffPenalties(entry)) } : undefined}
                  onClick={() => { if (hasEntry && !cell.muted) setSelectedEntry(entry); }}
                >
                  <div className={styles.dayNum}>{cell.day}</div>
                  {hasEntry && !cell.muted && (
                    <div className={styles.dayInfo}>
                      {entry.status === "Present" && entry.timeIn && entry.timeOut ? (
                        <>
                          <div className={styles.dayTime}>
                            <span className={styles.dayDot} style={{ background: "#27ae60" }} />
                            {fmtShort(entry.timeIn)} – {fmtShort(entry.timeOut)}
                          </div>
                          {(() => {
                            const suppress = suppressDayOffPenalties(entry);
                            const showLate = !suppress && entry.lateMinutes > 0;
                            const showUT   = !suppress && entry.undertimeMinutes > 0;
                            const showOT   = entry.overtimeHours > 0;
                            if (!showLate && !showUT && !showOT) return null;
                            return (
                              <div className={styles.dayTags}>
                                {showOT && (() => {
                                  // Colour the OT tag by approval state so the
                                  // employee can spot un-submitted / pending
                                  // overtime at a glance — those hours aren't
                                  // on the payslip yet.
                                  const ots = entry.overtimeStatus;
                                  const pending     = ots === "PENDING";
                                  const unsubmitted = ots == null;
                                  const rejected    = ots === "REJECTED";
                                  const color = (pending || unsubmitted) ? "#b8860b"
                                              : rejected ? "#c0392b"
                                              : "#2980b9";
                                  const prefix = pending ? "⏳ "
                                              : unsubmitted ? "⚠ "
                                              : rejected ? "✕ "
                                              : "";
                                  const title = pending ? "Pending approval — not yet on payslip"
                                              : unsubmitted ? "Not yet submitted for approval"
                                              : rejected ? "Rejected — not paid"
                                              : "Approved";
                                  return (
                                    <span className={styles.dayTag} style={{ color }} title={title}>
                                      <span className={styles.dayDot} style={{ background: color }} />
                                      {prefix}OT: {entry.overtimeHours}h
                                    </span>
                                  );
                                })()}
                                {showLate && (
                                  <span className={styles.dayTag} style={{ color: "#e67e22" }}>
                                    <span className={styles.dayDot} style={{ background: "#e67e22" }} />
                                    Late: {entry.lateMinutes}m
                                  </span>
                                )}
                                {showUT && (
                                  <span className={styles.dayTag} style={{ color: "#e74c3c" }}>
                                    <span className={styles.dayDot} style={{ background: "#e74c3c" }} />
                                    UT: {entry.undertimeMinutes}m
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          {/* Progress bar */}
                          <div className={styles.dayBar}>
                            <div className={styles.dayBarWorked} style={{ width: `${Math.min(100, ((entry.workedHours || 0) / 9) * 100)}%` }} />
                            {entry.overtimeHours > 0 && (
                              <div className={styles.dayBarOT} style={{ width: `${Math.min(30, (entry.overtimeHours / 3) * 30)}%` }} />
                            )}
                          </div>
                        </>
                      ) : (
                        (() => {
                          // For auto-created leave rows the remarks are
                          // formatted "<LeaveTitle> (<Duration>) [Auto#<id>]"
                          // — parse the title and duration to show them
                          // inline so the employee doesn't have to open
                          // the detail panel just to see what leave it is.
                          let label = entry.status;
                          let duration = null;
                          if (entry.status === "Leave" && entry.remarks) {
                            const m = entry.remarks.match(/^(.+?)\s*\(([^)]+)\)\s*\[Auto#\d+\]/);
                            if (m) { label = m[1].trim(); duration = m[2].trim(); }
                          }
                          return (
                            <div className={styles.dayStatus}>
                              <span className={styles.dayDot} style={{ background: statusColor(entry.status) }} />
                              {label}
                              {duration && (
                                <span style={{ marginLeft: 4, fontSize: 10, opacity: .7 }}>· {duration}</span>
                              )}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right Panel: Details ── */}
        <div className={styles.rightPanel}>
          {selectedEntry ? (
            <div className={styles.detailPanel}>
              <div className={styles.detailHeader}>
                <div className={styles.detailTitle}>Timesheet Details</div>
                <button className={styles.detailClose} onClick={() => setSelectedEntry(null)}>✕</button>
              </div>
              <div className={styles.detailBody}>
                <div className={styles.detailDate}>
                  {(() => {
                    const d = Array.isArray(selectedEntry.date)
                      ? `${selectedEntry.date[0]}-${String(selectedEntry.date[1]).padStart(2,"0")}-${String(selectedEntry.date[2]).padStart(2,"0")}`
                      : selectedEntry.date;
                    const dt = new Date(d + "T00:00:00");
                    const days = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
                    return `${MONTH_NAMES[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
                  })()}
                </div>

                {selectedEntry.schedStart && selectedEntry.schedEnd && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Scheduled</span>
                    <span className={styles.detailValue}>{fmt12(selectedEntry.schedStart)} – {fmt12(selectedEntry.schedEnd)}</span>
                  </div>
                )}
                {selectedEntry.timeIn && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Time In</span>
                    <span className={styles.detailValue}>{fmt12(selectedEntry.timeIn)}</span>
                  </div>
                )}
                {selectedEntry.timeOut && (
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Time Out</span>
                    <span className={styles.detailValue}>{fmt12(selectedEntry.timeOut)}</span>
                  </div>
                )}
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Break</span>
                  <span className={styles.detailValue}>{selectedEntry.breakMinutes ?? 0} mins</span>
                </div>
                <div className={styles.detailDivider} />
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Worked Hours</span>
                  <span className={styles.detailValue}>{selectedEntry.workedHours ?? 0} hrs</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Late</span>
                  <span className={styles.detailValue}>
                    {suppressDayOffPenalties(selectedEntry) ? 0 : (selectedEntry.lateMinutes ?? 0)} mins
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Undertime</span>
                  <span className={styles.detailValue}>
                    {suppressDayOffPenalties(selectedEntry) ? 0 : (selectedEntry.undertimeMinutes ?? 0)}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Overtime</span>
                  <span className={styles.detailValueHighlight}>
                    +{selectedEntry.overtimeHours ?? 0} hrs
                    {selectedEntry.overtimeHours > 0 && (() => {
                      const st = selectedEntry.overtimeStatus;
                      const pillStyle = {
                        marginLeft: 8, padding: "2px 8px", fontSize: 10, fontWeight: 700,
                        borderRadius: 999, letterSpacing: 0.3,
                      };
                      if (st === "PENDING")  return <span style={{ ...pillStyle, background: "#fef3c7", color: "#92400e" }}>PENDING</span>;
                      if (st === "APPROVED") return <span style={{ ...pillStyle, background: "#dcfce7", color: "#166534" }}>APPROVED</span>;
                      if (st === "REJECTED") return <span style={{ ...pillStyle, background: "#fee2e2", color: "#991b1b" }}>REJECTED</span>;
                      return <span style={{ ...pillStyle, background: "#f3f4f6", color: "#6b7280" }}>NOT YET REQUESTED</span>;
                    })()}
                  </span>
                </div>

                {/* OT approval workflow controls. Visibility:
                    – Owner sees Apply (when OT > 0 and not yet requested)
                      and Cancel (while pending).
                    – Manager (entry's reportsTo) and admin tier see
                      Approve / Reject while pending.
                    – Decided rows show who/when + the rejection note. */}
                {selectedEntry.overtimeHours > 0 && (() => {
                  const st        = selectedEntry.overtimeStatus;
                  const isOwner   = !!user?.employeeCode && user.employeeCode === selectedEntry.employeeCode;
                  const ownerRow  = (employees || []).find(e => e.code === selectedEntry.employeeCode);
                  const ownerMgr  = ownerRow?.reportsTo || "";
                  const isManager = !!user?.employeeCode && !!ownerMgr && user.employeeCode === ownerMgr;
                  const canDecide = (isAdmin || isManager) && st === "PENDING";
                  const decidedBy = selectedEntry.overtimeDecidedBy
                    ? ((employees || []).find(e => e.code === selectedEntry.overtimeDecidedBy)?.name
                       || selectedEntry.overtimeDecidedBy)
                    : null;
                  const decidedAt = selectedEntry.overtimeDecidedAt
                    ? new Date(selectedEntry.overtimeDecidedAt).toLocaleString()
                    : null;
                  return (
                    <div style={{ marginTop: 8, padding: "10px 12px", background: "#fafafa", borderRadius: 8, fontSize: 12 }}>
                      {isOwner && !st && (
                        <>
                          <div style={{ marginBottom: 6, color: "#6b7280" }}>
                            Overtime is not yet counted toward your salary. Submit it for approval to be paid.
                          </div>
                          <button
                            onClick={handleRequestOvertime}
                            style={{ padding: "6px 12px", border: "none", borderRadius: 6, background: "#c9a84c", color: "#fff", fontWeight: 600, cursor: "pointer" }}>
                            Apply for Overtime
                          </button>
                        </>
                      )}
                      {st === "PENDING" && (
                        <>
                          <div style={{ marginBottom: 8, color: "#92400e" }}>
                            ⏳ Pending {ownerMgr
                              ? `with ${(employees || []).find(e => e.code === ownerMgr)?.name || ownerMgr}`
                              : "with admin / owner"}.
                          </div>
                          {isOwner && (
                            <button
                              onClick={handleCancelOvertime}
                              style={{ padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#374151", cursor: "pointer", marginRight: 8 }}>
                              Cancel request
                            </button>
                          )}
                          {canDecide && (
                            <>
                              <button
                                onClick={() => handleDecideOvertime("APPROVED")}
                                style={{ padding: "6px 12px", border: "none", borderRadius: 6, background: "#16a34a", color: "#fff", fontWeight: 600, cursor: "pointer", marginRight: 8 }}>
                                Approve
                              </button>
                              <button
                                onClick={() => handleDecideOvertime("REJECTED")}
                                style={{ padding: "6px 12px", border: "none", borderRadius: 6, background: "#dc2626", color: "#fff", fontWeight: 600, cursor: "pointer" }}>
                                Reject
                              </button>
                            </>
                          )}
                        </>
                      )}
                      {st === "APPROVED" && (
                        <div style={{ color: "#166534" }}>
                          ✓ Approved{decidedBy ? ` by ${decidedBy}` : ""}{decidedAt ? ` on ${decidedAt}` : ""}.
                        </div>
                      )}
                      {st === "REJECTED" && (
                        <>
                          <div style={{ color: "#991b1b" }}>
                            ✗ Rejected{decidedBy ? ` by ${decidedBy}` : ""}{decidedAt ? ` on ${decidedAt}` : ""}.
                          </div>
                          {selectedEntry.overtimeDecisionNote && (
                            <div style={{ marginTop: 4, color: "#6b7280" }}>
                              Reason: {selectedEntry.overtimeDecisionNote}
                            </div>
                          )}
                          {isOwner && (
                            <button
                              onClick={handleRequestOvertime}
                              style={{ marginTop: 8, padding: "6px 12px", border: "none", borderRadius: 6, background: "#c9a84c", color: "#fff", fontWeight: 600, cursor: "pointer" }}>
                              Re-submit for approval
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}

                <div className={styles.detailDivider} />
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Status</span>
                  <span className={styles.detailStatusBadge} style={{ background: statusBg(selectedEntry.status), color: statusColor(selectedEntry.status) }}>
                    <span className={styles.detailStatusDot} style={{ background: statusColor(selectedEntry.status) }} />
                    {selectedEntry.status}
                    {selectedEntry.lateMinutes > 0 && selectedEntry.status === "Present" && (
                      <span className={styles.detailLateBadge}>Late</span>
                    )}
                  </span>
                </div>

                {selectedEntry.remarks && (
                  <>
                    <div className={styles.detailDivider} />
                    <div className={styles.detailRemarksLabel}>Remarks</div>
                    <div className={styles.detailRemarks}>{selectedEntry.remarks}</div>
                  </>
                )}

                <div className={styles.detailActions}>
                  <button className={styles.editBtn} onClick={() => setModalEntry(selectedEntry)}>Edit</button>
                  <button className={styles.deleteBtn} onClick={handleDelete}>Delete</button>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.emptyPanel}>
              <div className={styles.emptyIcon}>📋</div>
              <div className={styles.emptyTitle}>No Entry Selected</div>
              <div className={styles.emptyText}>Click on a day in the calendar<br />to see timesheet details.</div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {modalEntry !== undefined && selectedCode && (
        <TimesheetModal
          entry={modalEntry}
          employeeCode={selectedCode}
          onClose={() => setModalEntry(undefined)}
          onSaved={reload}
        />
      )}

      {/* Overtime Records Modal */}
      {otRecordsOpen && (
        <OvertimeRecordsModal
          records={otRecords}
          loading={otRecordsLoading}
          employees={employees}
          onClose={() => setOtRecordsOpen(false)}
          onOpenEntry={(ts) => {
            setOtRecordsOpen(false);
            // Jump the calendar to the entry's month + employee, then
            // pre-select the row so the right panel opens with the
            // approve / reject controls visible.
            if (ts?.employeeCode) setSelectedCode(ts.employeeCode);
            if (ts?.date) {
              const d = Array.isArray(ts.date)
                ? new Date(ts.date[0], ts.date[1] - 1, ts.date[2])
                : new Date(ts.date + "T00:00:00");
              if (!isNaN(d)) { setYear(d.getFullYear()); setMonth(d.getMonth()); }
            }
            setSelectedEntry(ts);
          }}
        />
      )}
    </div>
  );
}

function OvertimeRecordsModal({ records, loading, employees, onClose, onOpenEntry }) {
  const [filter, setFilter] = useState("ALL");
  const filtered = (records || []).filter(r => filter === "ALL" || r.overtimeStatus === filter);
  const nameOf = (code) => {
    if (!code) return "—";
    return (employees || []).find(e => e.code === code)?.name || code;
  };
  const statusChip = (s) => {
    const map = {
      PENDING:  { bg: "#fef3c7", fg: "#92400e" },
      APPROVED: { bg: "#dcfce7", fg: "#166534" },
      REJECTED: { bg: "#fee2e2", fg: "#991b1b" },
    };
    const c = map[s] || { bg: "#e5e7eb", fg: "#374151" };
    return (
      <span style={{
        display: "inline-block", fontSize: 10, fontWeight: 700,
        padding: "2px 8px", borderRadius: 999, letterSpacing: ".4px",
        background: c.bg, color: c.fg,
      }}>{s}</span>
    );
  };
  const fmtDate = (d) => Array.isArray(d)
    ? `${d[0]}-${String(d[1]).padStart(2, "0")}-${String(d[2]).padStart(2, "0")}`
    : d;
  return createPortal(
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}>
      <div style={{
        background: "#fff", borderRadius: 14, width: "min(900px, 100%)",
        maxHeight: "85vh", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,.25)",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: "1px solid #eee",
        }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Overtime Approval Records</div>
          <button onClick={onClose}
                  style={{ background: "#f3f4f6", border: "none", borderRadius: 999,
                           width: 28, height: 28, cursor: "pointer", fontSize: 14 }}>✕</button>
        </div>
        <div style={{ padding: "10px 20px", display: "flex", gap: 8, borderBottom: "1px solid #f1f5f9" }}>
          {["ALL", "PENDING", "APPROVED", "REJECTED"].map(k => (
            <button key={k} onClick={() => setFilter(k)}
                    style={{
                      padding: "4px 12px", borderRadius: 999, border: "1px solid #e5e7eb",
                      cursor: "pointer", fontSize: 12, fontWeight: 600,
                      background: filter === k ? "#111827" : "#fff",
                      color:      filter === k ? "#fff"    : "#374151",
                    }}>{k}</button>
          ))}
          <div style={{ marginLeft: "auto", fontSize: 11, color: "#6b7280", alignSelf: "center" }}>
            {loading ? "Loading..." : `${filtered.length} record(s)`}
          </div>
        </div>
        <div style={{ overflow: "auto", flex: 1 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
              No {filter === "ALL" ? "" : filter.toLowerCase() + " "}records.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", color: "#374151", fontSize: 11,
                             textAlign: "left", textTransform: "uppercase", letterSpacing: ".4px" }}>
                  <th style={{ padding: "10px 12px" }}>Employee</th>
                  <th style={{ padding: "10px 12px" }}>Date</th>
                  <th style={{ padding: "10px 12px" }}>Hours</th>
                  <th style={{ padding: "10px 12px" }}>Status</th>
                  <th style={{ padding: "10px 12px" }}>Decided by / at</th>
                  <th style={{ padding: "10px 12px" }}>Note</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const decided = r.overtimeDecidedBy
                    ? `${nameOf(r.overtimeDecidedBy)}${r.overtimeDecidedAt ? " · " + new Date(r.overtimeDecidedAt).toLocaleString() : ""}`
                    : "—";
                  return (
                    <tr key={r.id}
                        onClick={() => onOpenEntry(r)}
                        style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "10px 12px", fontWeight: 600 }}>{nameOf(r.employeeCode)}</td>
                      <td style={{ padding: "10px 12px", color: "#4b5563" }}>{fmtDate(r.date)}</td>
                      <td style={{ padding: "10px 12px", color: "#4b5563" }}>
                        {r.overtimeHours || 0} hr{r.overtimeHours === 1 ? "" : "s"}
                      </td>
                      <td style={{ padding: "10px 12px" }}>{statusChip(r.overtimeStatus)}</td>
                      <td style={{ padding: "10px 12px", color: "#4b5563", fontSize: 12 }}>{decided}</td>
                      <td style={{ padding: "10px 12px", color: "#374151",
                                   maxWidth: 240, whiteSpace: "pre-wrap" }}>
                        {r.overtimeDecisionNote || <span style={{ color: "#9ca3af" }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
